import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { SessionTabs } from '../components/SessionTabs.js';
import { redactString } from '../lib/phi.js';

// Match-tier → chip tint. Whatever the exporter recorded shows verbatim; known
// tiers get a color so dedup/insert decisions are scannable.
const TIER_COLOR: Record<string, string> = {
  id_map: 'rgba(148,163,184,0.20)',
  pharm1: 'rgba(13,207,184,0.20)',
  ext_ref: 'rgba(13,207,184,0.20)',
  review: 'rgba(250,204,21,0.20)',
  insert: 'rgba(168,85,247,0.20)',
  tier_5: 'rgba(168,85,247,0.20)',
};

/**
 * Stage-4 transparency: the source → Cerbo id trail. For every source record the
 * import touched, shows the resulting Cerbo record id and the tier at which it
 * matched/deduped/inserted — the audit answer to "where did this record land, and
 * did it link to an existing one?". Source ids are PHI-redacted (MRN-shaped values
 * mask; opaque guids pass through); Cerbo ids are internal row ids, shown raw.
 */
export function IdMapPage({ sessionId }: { sessionId: string }) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['id-map', sessionId, search, typeFilter],
    queryFn: () =>
      api.listIdMap(sessionId, { search: search || undefined, canonicalType: typeFilter || undefined, limit: 500 }),
  });
  const rows = data?.rows ?? [];
  const summary = data?.summary ?? [];

  const byType = new Map<string, number>();
  for (const s of summary) byType.set(s.canonicalType, (byType.get(s.canonicalType) ?? 0) + s.count);
  const tierColor = (t: string) => TIER_COLOR[t] ?? 'rgba(148,163,184,0.18)';

  return (
    <div className="space-y-4 max-w-5xl mx-auto p-6">
      <SessionTabs sessionId={sessionId} active="cerbo-ids" />
      <div>
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Cerbo IDs</h1>
        <div className="text-[12px]" style={{ color: 'var(--text-dimmed)' }}>
          stage 4 · every source record → its resulting Cerbo id + dedup / match tier · {data?.total ?? 0} mapped
        </div>
      </div>

      {byType.size > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--text-dimmed)' }}>Type</span>
          {[...byType.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([t, n]) => (
              <button
                key={t}
                type="button"
                onClick={() => setTypeFilter(typeFilter === t ? null : t)}
                className="rounded-full px-2 py-0.5 text-[11px]"
                style={{
                  background: typeFilter === t ? 'var(--accent-blue)22' : 'var(--bg-tertiary)',
                  border: `1px solid ${typeFilter === t ? 'var(--accent-blue)' : 'var(--border)'}`,
                  color: 'var(--text-secondary)',
                }}
              >
                {t} · {n}
              </button>
            ))}
          {typeFilter && (
            <button type="button" onClick={() => setTypeFilter(null)} className="ml-1 text-[11px] underline" style={{ color: 'var(--text-dimmed)' }}>
              clear
            </button>
          )}
        </div>
      )}

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search source id…"
        className="w-full rounded-md px-3 py-1.5 text-[12px]"
        style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
      />

      {isLoading ? (
        <div className="text-[12px]" style={{ color: 'var(--text-dimmed)' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-md p-4 text-[12px]" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-dimmed)' }}>
          {search || typeFilter ? 'No id-map entries match the filter.' : 'No id-map entries yet — run an import to populate the source → Cerbo id trail.'}
        </div>
      ) : (
        <div className="rounded-md overflow-hidden" style={{ border: '1px solid var(--border)' }}>
          <table className="w-full text-[12px]">
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)' }}>
                {['Source ID', 'Type', 'Cerbo ID', 'Match tier'].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest" style={{ color: 'var(--text-dimmed)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.canonicalType}-${r.sourceId}-${i}`} style={{ borderTop: '1px solid var(--border)' }}>
                  <td className="px-3 py-1.5 font-mono" style={{ color: 'var(--text-secondary)' }}>{redactString(r.sourceId)}</td>
                  <td className="px-3 py-1.5" style={{ color: 'var(--text-dimmed)' }}>{r.canonicalType}</td>
                  <td className="px-3 py-1.5 font-mono" style={{ color: 'var(--accent-blue)' }}>{r.cerboId}</td>
                  <td className="px-3 py-1.5">
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-mono" style={{ background: tierColor(r.matchTier), color: 'var(--text-primary)' }}>{r.matchTier}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-[10px]" style={{ color: 'var(--text-dimmed)' }}>
        Source IDs are PHI-redacted; Cerbo IDs are internal record ids. Showing up to 500 rows.
      </div>
    </div>
  );
}
